import { BarLabel, BarLabelProps, MarkElementProps } from '@mui/x-charts';
import { useYScale } from '@mui/x-charts/hooks/useScale';
import { useBarSeries, useLineSeries } from '@mui/x-charts/hooks/useSeries';
import { animated, SpringValue, to, useSpring } from '@react-spring/web';
import React from 'react';
import {
  getBarLabelY,
  getFinalBarHeight,
} from '../../bar-chart/bar-chart-utils';
import { BENCHMARK_DATA_PREFIX } from '../common-chart-utils/common-chart-configs';

export const getMarkLabelText = (
  value: number | null | undefined,
  seriesId: string | number,
  formatter: (value: number) => string | null | undefined
): string => {
  if (value === null || value === undefined) {
    return '';
  }
  // Missing benchmark points are filled with 0 upstream, so a benchmark 0 is not a real value
  if (value === 0 && String(seriesId).startsWith(`${BENCHMARK_DATA_PREFIX}-`)) {
    return '';
  }
  return formatter(value) || '';
};

export const MarkWithLabel = (props: MarkElementProps) => {
  const { x, y, id, dataIndex } = props;

  const series = useLineSeries()?.series[id];

  const position = useSpring({ to: { x, y }, immediate: true });
  const value = series?.data?.[dataIndex];
  const formattedValue = series
    ? getMarkLabelText(value, id, (v) =>
        series.valueFormatter(v, { dataIndex })
      )
    : '';

  return (
    <React.Fragment>
      <animated.text
        x={position.x}
        y={position.y}
        transform="translate(0, -8)"
        textAnchor="middle"
        alignmentBaseline="baseline"
        fontSize={12}
      >
        {formattedValue}
      </animated.text>
    </React.Fragment>
  );
};

type AnimatedBarLabelStyle = {
  y?: SpringValue<number>;
  height?: SpringValue<number>;
};

// MUI centers bar labels inside the bar; this moves labels of bars too short to hold them just above the bar
export const BarLabelWithSmallBarPlacement = (props: BarLabelProps) => {
  const { seriesId, dataIndex, style } = props;
  const series = useBarSeries()?.series[seriesId];
  const yScale = useYScale(series?.yAxisId ?? series?.yAxisKey);
  const value = series?.data?.[dataIndex] ?? null;
  // Decide from the final bar height: the animated height starts at 0 for every bar
  const finalBarHeight = getFinalBarHeight(
    series?.stackedData?.[dataIndex],
    yScale as (value: number) => number | undefined
  );
  const animatedStyle = style as AnimatedBarLabelStyle | undefined;
  if (!animatedStyle?.y || !animatedStyle?.height) {
    return <BarLabel {...props} />;
  }
  const y = to([animatedStyle.y, animatedStyle.height], (labelY, barHeight) =>
    getBarLabelY(labelY, barHeight, finalBarHeight, value)
  );
  return (
    <BarLabel
      {...props}
      style={{ ...style, y } as unknown as BarLabelProps['style']}
    />
  );
};
